'use client'

import { useEffect, useMemo, useState } from 'react'

import {
  resolveDisplayImageUrl,
  resolveDisplayImageUrlSync,
  resolveRemoteDisplayUrl,
  resolveS3FallbackDisplayUrl
} from './resolve'
import type { ImageDisplaySource } from './types'

interface UseDisplayImageUrlResult {
  displayUrl: string
  previewUrl: string
  s3FallbackUrl: string
  isLoading: boolean
}

function hasMemoryBlob(source?: ImageDisplaySource | null): boolean {
  if (!source) return false
  return (
    (typeof source.localPreviewUrl === 'string' && source.localPreviewUrl.startsWith('blob:')) ||
    (typeof source.previewUrl === 'string' && source.previewUrl.startsWith('blob:'))
  )
}

export function useDisplayImageUrl(
  source?: ImageDisplaySource | null
): UseDisplayImageUrlResult {
  const syncUrl = useMemo(() => resolveDisplayImageUrlSync(source), [source])
  const remoteUrl = useMemo(() => resolveRemoteDisplayUrl(source), [source])
  const s3FallbackUrl = useMemo(() => resolveS3FallbackDisplayUrl(source), [source])

  const memoryBlob = hasMemoryBlob(source)
  const hasLocalCacheKey = !!source?.localCacheKey
  const preferLocalFirst = hasLocalCacheKey || memoryBlob

  // 有 localCacheKey 时也先展示远端 URL，避免 IndexedDB 缺失时图片空白
  const initialUrl = syncUrl || remoteUrl

  const [displayUrl, setDisplayUrl] = useState(initialUrl)
  const [isLoading, setIsLoading] = useState(preferLocalFirst && !memoryBlob && !!remoteUrl)

  useEffect(() => {
    let cancelled = false

    if (!source) {
      setDisplayUrl('')
      setIsLoading(false)
      return
    }

    if (memoryBlob) {
      setDisplayUrl(syncUrl)
      setIsLoading(false)
      return
    }

    if (!hasLocalCacheKey) {
      setDisplayUrl(syncUrl || remoteUrl)
      setIsLoading(false)
      return
    }

    setDisplayUrl(syncUrl || remoteUrl)
    setIsLoading(true)

    void resolveDisplayImageUrl(source)
      .then(url => {
        if (cancelled) return
        setDisplayUrl(url || remoteUrl)
        setIsLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        setDisplayUrl(remoteUrl)
        setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [source, syncUrl, remoteUrl, memoryBlob, hasLocalCacheKey])

  return {
    displayUrl,
    previewUrl: displayUrl,
    s3FallbackUrl,
    isLoading
  }
}
